<script setup lang="ts">
// One inline, in-flow notice (issue 86): the block-level sibling of the floating
// NoticeToast and the blocking NoticeDialog. It sits INSIDE a section to explain
// its state — e.g. an error on a posting action, or the info that external
// posting is disabled by default in a demo workspace (decision 25).
//
// Presentational only (ADR 0014): the lib owns the LOOK; every user-facing string
// comes from the host via props/slots, so each app owns its ×9 translations. No
// state, no timers, no logic. Prop shape mirrors NoticeToast for consistency.
//
// `warning` is the third variant NoticeToast already had (issue 195): a statement
// that something is off about what IS on screen, rather than a failure that took its
// place. It is deliberately not `error` — it keeps `role="status"`/polite, because
// interrupting a screen reader is wrong when the user still has usable content in
// front of them.
//
// `success` (packet suite-design-ats-M5-p2): NoticeToast/NoticeDialog already carry
// it — an in-flow positive result (e.g. a workflow that validates clean) needs the
// same tone inline instead of being flattened to `info`. Same `role="status"`/polite
// as info/warning.
import LumiIcon from '../icons/LumiIcon.vue';
withDefaults(
  defineProps<{
    variant?: 'error' | 'warning' | 'info' | 'success';
    title?: string;
    dismissible?: boolean;
    dismissLabel?: string;
  }>(),
  { variant: 'info', dismissible: false, dismissLabel: 'Schließen' },
);

defineEmits<{ dismiss: [] }>();
</script>

<template>
  <div
    class="lumi-inline-notice"
    :class="`lumi-inline-notice--${variant}`"
    :role="variant === 'error' ? 'alert' : 'status'"
    :aria-live="variant === 'error' ? 'assertive' : 'polite'"
  >
    <span class="lumi-inline-notice__icon" aria-hidden="true">
      <slot name="icon">
        <!-- error -->
        <LumiIcon v-if="variant === 'error'" name="error" width="18" height="18" stroke-width="2" />
        <!-- warning -->
        <LumiIcon v-else-if="variant === 'warning'" name="warning" width="18" height="18" stroke-width="2" />
        <!-- success -->
        <LumiIcon v-else-if="variant === 'success'" name="done" width="18" height="18" stroke-width="2" />
        <!-- info -->
        <LumiIcon v-else name="info" width="18" height="18" stroke-width="2" />
      </slot>
    </span>
    <div class="lumi-inline-notice__content">
      <p v-if="title" class="lumi-inline-notice__title">{{ title }}</p>
      <div class="lumi-inline-notice__message"><slot /></div>
      <div v-if="$slots.action" class="lumi-inline-notice__action"><slot name="action" /></div>
    </div>
    <button
      v-if="dismissible"
      type="button"
      class="lumi-inline-notice__close"
      :aria-label="dismissLabel"
      @click="$emit('dismiss')"
    >
      <LumiIcon name="remove" width="16" height="16" stroke-width="2" />
    </button>
  </div>
</template>

<style>
/* Spec §8: functional soft tints with the matching text colour, no left stripe. */
.lumi-inline-notice{display:flex;gap:var(--space-3);align-items:flex-start;border-radius:var(--radius-md);padding:var(--space-3) var(--space-4);font-family:var(--font-body);}
.lumi-inline-notice__icon{flex:none;display:flex;padding-top:1px;}
.lumi-inline-notice--error{background:var(--danger-soft);color:var(--danger-text);}
.lumi-inline-notice--warning{background:var(--warning-soft);color:var(--warning-text);}
.lumi-inline-notice--info{background:var(--info-soft);color:var(--info-text);}
.lumi-inline-notice--success{background:var(--success-soft);color:var(--success-text);}
.lumi-inline-notice__content{min-width:0;flex:1;}
.lumi-inline-notice__title{font-size:var(--text-sm);font-weight:var(--weight-semibold);margin:0 0 2px;}
.lumi-inline-notice__message{font-size:var(--text-sm);line-height:1.45;word-wrap:break-word;}
.lumi-inline-notice__action{margin-top:var(--space-2);}
.lumi-inline-notice__close{flex:none;border:none;background:none;cursor:pointer;color:inherit;opacity:.7;padding:2px;border-radius:var(--radius-sm);display:flex;}
.lumi-inline-notice__close:hover{opacity:1;}
</style>
